var _jsxFileName = "/codebuild/output/src454737571/src/bitbucket.org/newyuinc/adc-polaris-web/src/components/consents/NoticesConsents.tsx";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, InformationalScreen, LoadingSpinner, Stack, Text } from '@adc/parallax-component-library';
import axios from 'axios';
import Document from 'Components/consents/Document';
import { MainContainer, RichText } from 'Components/utility';
import { CLOSE_MODAL, SHOW_MODAL } from 'Reducers/modal/types';
import { notifyApp } from 'Utilities/appCommunicator';
import { getDocumentPathFromLanguage } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { getSessionObject, removeSessionObject, setSessionObject } from 'Utilities/storage';
import { store } from 'Utilities/store';
import { handleConsent } from 'Services/consent';
const PageIndexes = {
  TERMS_OF_USE: 0,
  PRIVACY_NOTICE: 1,
  HIPAA: 2,
  RWE: 3,
  HIPAA_OUS: 4
};
const accepted = new Set([]);
const declined = new Set([]);
const mapStateToProps = ({
  env: {
    osType,
    osVersion,
    app,
    appVersion,
    phoneLanguage
  },
  auth,
  config: {
    documentUrls,
    documentPaths
  }
}) => {
  var _documentPaths$cms, _documentPaths$cms2, _documentPaths$cms3;
  return {
    osType,
    osVersion,
    app,
    appVersion,
    consents: auth.include.patient.consents,
    accountId: auth.account_id,
    country: auth.include.patient.country,
    cmsDomain: documentUrls.cms,
    cmsStrapiDomain: documentUrls.cmsStrapi,
    hipaaCmsPaths: (_documentPaths$cms = documentPaths.cms) === null || _documentPaths$cms === void 0 ? void 0 : _documentPaths$cms.hipaalibre,
    rweCmsPaths: (_documentPaths$cms2 = documentPaths.cms) === null || _documentPaths$cms2 === void 0 ? void 0 : _documentPaths$cms2.rwelibre,
    crmCmsPaths: (_documentPaths$cms3 = documentPaths.cms) === null || _documentPaths$cms3 === void 0 ? void 0 : _documentPaths$cms3.crm,
    phoneLanguage
  };
};
const connector = connect(mapStateToProps);
const NoticesConsents = ({
  osType,
  osVersion,
  app,
  appVersion,
  consents: {
    touLibre,
    pp,
    hipaa,
    crm,
    realWorldEvidence
  },
  accountId,
  cmsDomain,
  cmsStrapiDomain,
  hipaaCmsPaths,
  rweCmsPaths,
  crmCmsPaths,
  phoneLanguage,
  country
}) => {
  const [loading, setLoading] = useState(true);
  const [currentPageIndex, setCurrentPageIndex] = useState(PageIndexes.TERMS_OF_USE);
  const [hipaaBodyText, setHipaaBodyText] = useState(i18n.t('Hipaa.content.hipaaRichText.bodyText'));
  const [hipaaOUSBodyText, setHipaaOUSBodyText] = useState(i18n.t('HealthcareAndMarketingOus.content.informationConsent.bodyText'));
  const [rweBodyText, setRweBodyText] = useState(i18n.t('Research.content.researchRichText.bodyText'));
  const isConsentOutdated = useCallback(consent => {
    if (!consent || !consent.latestVersion) {
      return false;
    }
    return !consent.at || consent.at < consent.latestVersion;
  }, []);
  const dispatchModal = useCallback((details, testID) => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        details,
        testID
      }
    });
  }, []);
  const handleError = useCallback(error => {
    notifyApp('adc-webview:notices-consents', {
      error: {
        message: error.message,
        status: error.status
      }
    });
  }, []);
  const verifyConsents = useCallback(async () => {
    const isUS = country === 'US';
    const isHealthConsentOutdated = isConsentOutdated(isUS ? hipaa : crm);
    const isRweOutdated = isConsentOutdated(realWorldEvidence);
    if (!isHealthConsentOutdated && !isRweOutdated) {
      notifyApp('adc-webview:notices-consents', {
        declined,
        accepted
      });
      return;
    }
    try {
      const fetchPromises = [];
      if (isHealthConsentOutdated) {
        const cmsPaths = isUS ? hipaaCmsPaths : crmCmsPaths;
        const domain = isUS ? cmsDomain : cmsStrapiDomain;
        const cmsUrl = getDocumentPathFromLanguage(cmsPaths, phoneLanguage);
        fetchPromises.push(axios.get(`${domain}${cmsUrl}`).then(({
          data
        }) => {
          if (isUS) {
            setHipaaBodyText(data.Hipaa.content.hipaaRichText.bodyText);
          } else {
            setHipaaOUSBodyText(data.HealthcareAndMarketingOus.content.informationConsent.bodyText);
          }
        }));
      }
      if (isRweOutdated) {
        const rweCmsUrl = getDocumentPathFromLanguage(rweCmsPaths, phoneLanguage);
        fetchPromises.push(axios.get(`${cmsStrapiDomain}${rweCmsUrl}`).then(({
          data
        }) => {
          setRweBodyText(data.Research.content.researchRichText.bodyText);
        }));
      }
      await Promise.all(fetchPromises);
      const nextPageIndex = isHealthConsentOutdated ? isUS ? PageIndexes.HIPAA : PageIndexes.HIPAA_OUS : PageIndexes.RWE;
      setCurrentPageIndex(nextPageIndex);
      dispatchModal(i18n.t('Consents.modals.reviewConsent.body'), 'ReviewConsent');
      setLoading(false);
    } catch (err) {
      handleError(err);
    }
  }, [country, isConsentOutdated, hipaa, crm, realWorldEvidence, dispatchModal, hipaaCmsPaths, crmCmsPaths, cmsDomain, cmsStrapiDomain, phoneLanguage, rweCmsPaths, handleError]);
  const verifyNotices = useCallback(() => {
    const currentPageIndexSession = getSessionObject('currentPageIndex');
    if (currentPageIndexSession) {
      setCurrentPageIndex(currentPageIndexSession);
      removeSessionObject('currentPageIndex');
      setLoading(false);
      return;
    }
    if (isConsentOutdated(touLibre) || isConsentOutdated(pp)) {
      setCurrentPageIndex(isConsentOutdated(touLibre) ? PageIndexes.TERMS_OF_USE : PageIndexes.PRIVACY_NOTICE);
      dispatchModal(i18n.t('Consents.modals.reviewLegalNotice.body'), 'ReviewLegalNotice');
      setLoading(false);
      return;
    }
    verifyConsents();
  }, [dispatchModal, isConsentOutdated, pp, touLibre, verifyConsents]);

  // Init
  useEffect(() => {
    verifyNotices();
  }, [verifyNotices]);
  const addAcceptedConsent = useCallback(consent => {
    accepted.add(consent);
    declined.delete(consent);
  }, []);
  const addDeclinedConsent = useCallback(consent => {
    declined.add(consent);
    accepted.delete(consent);
  }, []);
  const displayNoticeDeclineModal = useCallback(id => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        details: /*#__PURE__*/React.createElement(Stack, {
          gap: "$4",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 231,
            columnNumber: 13
          }
        }, /*#__PURE__*/React.createElement(Text, {
          color: "$text.100",
          testID: "Consents.modals.acceptLegalNotice.title",
          fontSize: "$bodyBase.strong",
          fontWeight: "$bodyBase.strong",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 232,
            columnNumber: 15
          }
        }, i18n.t('Consents.modals.acceptLegalNotice.title')), /*#__PURE__*/React.createElement(Text, {
          testID: "Consents.modals.acceptLegalNotice.body",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 240,
            columnNumber: 15
          }
        }, i18n.t('Consents.modals.acceptLegalNotice.body'))),
        buttonLayout: 'column',
        primaryAction: {
          destructive: true,
          label: i18n.t('Consents.modals.acceptLegalNotice.declineAnyway'),
          onPress: () => {
            addDeclinedConsent(id);
            notifyApp('adc-webview:notices-consents', {
              declined,
              accepted
            });
            store.dispatch({
              type: CLOSE_MODAL
            });
          }
        },
        secondaryAction: {
          label: i18n.t('Global.microcopy.common.back'),
          onPress: () => store.dispatch({
            type: CLOSE_MODAL
          })
        },
        testID: 'AcceptLegalNotice'
      }
    });
  }, [addDeclinedConsent]);
  const updateConsent = useCallback(async (id, action) => {
    setLoading(true);
    await handleConsent(accountId, action, osType, osVersion, app, appVersion, id);
    setLoading(false);
  }, [accountId, app, appVersion, osType, osVersion]);
  const handleAccept = useCallback(async () => {
    switch (currentPageIndex) {
      case PageIndexes.TERMS_OF_USE:
        {
          try {
            await updateConsent('touLibre', 'accept');
            addAcceptedConsent('touLibre');
            if (isConsentOutdated(pp)) {
              setCurrentPageIndex(PageIndexes.PRIVACY_NOTICE);
            } else {
              verifyConsents();
            }
          } catch (err) {
            const error = err;
            handleError(error);
          }
          break;
        }
      case PageIndexes.PRIVACY_NOTICE:
        {
          try {
            await updateConsent('pp', 'accept');
            addAcceptedConsent('pp');
            verifyConsents();
          } catch (err) {
            const error = err;
            handleError(error);
          }
          break;
        }
      case PageIndexes.HIPAA:
        {
          try {
            await updateConsent('hipaa', 'accept');
            addAcceptedConsent('hipaa');
            if (isConsentOutdated(realWorldEvidence)) {
              setCurrentPageIndex(PageIndexes.RWE);
            } else {
              notifyApp('adc-webview:notices-consents', {
                declined,
                accepted
              });
            }
          } catch (err) {
            const error = err;
            handleError(error);
          }
          break;
        }
      case PageIndexes.RWE:
        {
          try {
            await updateConsent('realWorldEvidence', 'accept');
            addAcceptedConsent('realWorldEvidence');
            notifyApp('adc-webview:notices-consents', {
              declined,
              accepted
            });
          } catch (err) {
            const error = err;
            handleError(error);
          }
          break;
        }
      case PageIndexes.HIPAA_OUS:
        {
          try {
            await updateConsent('crm', 'accept');
            addAcceptedConsent('crm');
            if (isConsentOutdated(realWorldEvidence)) {
              setCurrentPageIndex(PageIndexes.RWE);
            } else {
              notifyApp('adc-webview:notices-consents', {
                declined,
                accepted
              });
            }
          } catch (err) {
            const error = err;
            handleError(error);
          }
          break;
        }
    }
  }, [addAcceptedConsent, currentPageIndex, handleError, isConsentOutdated, pp, realWorldEvidence, updateConsent, verifyConsents]);
  const handleDecline = useCallback(async () => {
    switch (currentPageIndex) {
      case PageIndexes.TERMS_OF_USE:
        displayNoticeDeclineModal('touLibre');
        break;
      case PageIndexes.PRIVACY_NOTICE:
        displayNoticeDeclineModal('pp');
        break;
      case PageIndexes.HIPAA:
        {
          try {
            await updateConsent('hipaa', 'decline');
            addDeclinedConsent('hipaa');
            if (isConsentOutdated(realWorldEvidence)) {
              setCurrentPageIndex(PageIndexes.RWE);
            } else {
              notifyApp('adc-webview:notices-consents', {
                declined,
                accepted
              });
            }
          } catch (err) {
            const error = err;
            handleError(error);
          }
          break;
        }
      case PageIndexes.RWE:
        {
          try {
            await updateConsent('realWorldEvidence', 'decline');
            addDeclinedConsent('realWorldEvidence');
            notifyApp('adc-webview:notices-consents', {
              declined,
              accepted
            });
          } catch (err) {
            const error = err;
            handleError(error);
          }
          break;
        }
      case PageIndexes.HIPAA_OUS:
        {
          try {
            await updateConsent('crm', 'decline');
            addDeclinedConsent('crm');
            if (isConsentOutdated(realWorldEvidence)) {
              setCurrentPageIndex(PageIndexes.RWE);
            } else {
              notifyApp('adc-webview:notices-consents', {
                declined,
                accepted
              });
            }
          } catch (err) {
            const error = err;
            handleError(error);
          }
          break;
        }
      default:
        break;
    }
  }, [addDeclinedConsent, currentPageIndex, displayNoticeDeclineModal, handleError, isConsentOutdated, realWorldEvidence, updateConsent]);
  const pageTitle = useMemo(() => {
    switch (currentPageIndex) {
      case PageIndexes.TERMS_OF_USE:
        return i18n.t('Global.microcopy.common.termsOfUse');
      case PageIndexes.PRIVACY_NOTICE:
        return i18n.t('Global.microcopy.common.privacyNotice');
      case PageIndexes.HIPAA:
        return i18n.t('Hipaa.title');
      case PageIndexes.RWE:
        return i18n.t('Research.title');
      case PageIndexes.HIPAA_OUS:
        return i18n.t('HealthcareAndMarketingOus.title');
      default:
        return '';
    }
  }, [currentPageIndex]);
  if (loading) {
    return /*#__PURE__*/React.createElement(MainContainer, {
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 502,
        columnNumber: 7
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 503,
        columnNumber: 9
      }
    }, /*#__PURE__*/React.createElement(LoadingSpinner, {
      "aria-label": "Loading",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 504,
        columnNumber: 11
      }
    })));
  }
  return /*#__PURE__*/React.createElement(MainContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 511,
      columnNumber: 5
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen, {
    topNavigationProps: {
      title: pageTitle
    },
    testID: "NoticesConsents.title",
    index: currentPageIndex,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 512,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 519,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Content, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 520,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(Document, {
    type: "termsOfUse",
    hideHeader: true,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 521,
      columnNumber: 13
    }
  })), /*#__PURE__*/React.createElement(InformationalScreen.Footer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 523,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    alignSelf: "flex-end",
    width: "100%",
    gap: "$4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 524,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(Button, {
    onPress: handleAccept,
    testID: "TermsOfUse.accept",
    label: i18n.t('Global.microcopy.common.accept'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 525,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "tertiary",
    onPress: handleDecline,
    label: i18n.t('Global.microcopy.common.decline'),
    testID: "TermsOfUse.decline",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 530,
      columnNumber: 15
    }
  })))), /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 539,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Content, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 540,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(Document, {
    type: "privacyNotice",
    hideHeader: true,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 541,
      columnNumber: 13
    }
  })), /*#__PURE__*/React.createElement(InformationalScreen.Footer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 543,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    alignSelf: "flex-end",
    width: "100%",
    gap: "$4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 544,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(Button, {
    onPress: handleAccept,
    testID: "PrivacyNotice.accept",
    label: i18n.t('Global.microcopy.common.accept'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 545,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "tertiary",
    onPress: handleDecline,
    label: i18n.t('Global.microcopy.common.decline'),
    testID: "PrivacyNotice.decline",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 550,
      columnNumber: 15
    }
  })))), /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 559,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Content, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 560,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(RichText, {
    testID: "Hipaa.content.hipaaRichText.bodyText",
    text: hipaaBodyText,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 561,
      columnNumber: 13
    }
  })), /*#__PURE__*/React.createElement(InformationalScreen.Footer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 563,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    alignSelf: "flex-end",
    width: "100%",
    gap: "$4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 564,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(Button, {
    onPress: handleAccept,
    testID: "Hipaa.accept",
    label: i18n.t('Global.microcopy.common.accept'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 565,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "tertiary",
    onPress: handleDecline,
    label: i18n.t('Global.microcopy.common.decline'),
    testID: "Hipaa.decline",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 570,
      columnNumber: 15
    }
  })))), /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 579,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Content, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 580,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(RichText, {
    testID: "Research.content.researchRichText.bodyText",
    text: rweBodyText,
    onPressLink: href => {
      // Save the current page index in the session so the user can return to the same page
      setSessionObject('currentPageIndex', PageIndexes.RWE);
      mediator.publish('router:navigate', href);
    },
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 581,
      columnNumber: 13
    }
  })), /*#__PURE__*/React.createElement(InformationalScreen.Footer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 591,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    alignSelf: "flex-end",
    width: "100%",
    gap: "$4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 592,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(Button, {
    onPress: handleAccept,
    testID: "RWE.optIn",
    label: i18n.t('Global.microcopy.common.optIn'),
    "aria-label": i18n.t('Global.microcopy.common.optIn'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 593,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "tertiary",
    onPress: handleDecline,
    label: i18n.t('Global.microcopy.common.decline'),
    "aria-label": i18n.t('Global.microcopy.common.decline'),
    testID: "RWE.decline",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 599,
      columnNumber: 15
    }
  })))), /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 609,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Content, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 610,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(RichText, {
    testID: "HealthcareAndMarketingOus.content.informationConsent.bodyText",
    text: hipaaOUSBodyText,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 611,
      columnNumber: 13
    }
  })), /*#__PURE__*/React.createElement(InformationalScreen.Footer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 616,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    alignSelf: "flex-end",
    width: "100%",
    gap: "$4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 617,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(Button, {
    onPress: handleAccept,
    testID: "HealthcareAndMarketingOus.accept",
    label: i18n.t('Global.microcopy.common.accept'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 618,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "tertiary",
    onPress: handleDecline,
    label: i18n.t('Global.microcopy.common.decline'),
    testID: "HealthcareAndMarketingOus.decline",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 623,
      columnNumber: 15
    }
  }))))));
};
export default connector(NoticesConsents);